'use client';

import { useState, useCallback, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import moment from 'moment';
import type { Moment } from 'moment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useQuery } from '@apollo/client/react';
import { cityCalendarEventDatesQuery } from '@/apollo/cities';
import { filterableRoute } from '@/utils/routes';
import ServerDay from '@/components/calendar/server-day';
import MiniCalendarHeader from '@/components/calendar/mini-calendar-header';

type Props = {
  cityId: string;
};

const MiniCalendar = ({ cityId }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const startParam = searchParams.get('start');
  const value = useMemo(
    () => (startParam ? moment(startParam) : null),
    [startParam],
  );

  const [displayedMonth, setDisplayedMonth] = useState<Moment>(
    () => (value && value.isValid() ? value.clone().startOf('month') : moment().startOf('month')),
  );

  const monthStart = displayedMonth.clone().startOf('month').toISOString();
  const monthEnd = displayedMonth.clone().endOf('month').toISOString();

  const { data } = useQuery(cityCalendarEventDatesQuery, {
    variables: { id: cityId, start: monthStart, end: monthEnd },
    fetchPolicy: 'cache-and-network',
  });

  const highlightedDays = useMemo(() => {
    if (!data?.city?.calendarEventDates) return [];
    return data.city.calendarEventDates.map((d: string) => moment.utc(d).date());
  }, [data]);

  const handleMonthChange = useCallback((date: Moment) => {
    setDisplayedMonth(date.clone().startOf('month'));
  }, []);

  const handleDateChange = useCallback((date: Moment | null) => {
    if (!date) return;

    const params: Record<string, string> = {};
    searchParams.forEach((v, k) => { params[k] = v; });

    const isToday = date.isSame(moment(), 'day');

    if (isToday) {
      delete params.start;
      delete params.page;
    } else {
      params.start = date.format('YYYY-MM-DD');
      delete params.page;
    }

    const { filter, type, categoryIds, start } = params;
    router.push(filterableRoute('/calendar', { filter, type, categoryIds, start }));
  }, [router, searchParams]);

  const handleGoToToday = useCallback(() => {
    setDisplayedMonth(moment().startOf('month'));

    const params: Record<string, string> = {};
    searchParams.forEach((v, k) => { params[k] = v; });
    delete params.start;
    delete params.page;

    const { filter, type, categoryIds } = params;
    router.push(filterableRoute('/calendar', { filter, type, categoryIds }));
  }, [router, searchParams]);

  return (
    <DateCalendar
      value={value}
      referenceDate={displayedMonth}
      onChange={handleDateChange}
      onMonthChange={handleMonthChange}
      reduceAnimations
      views={['day']}
      slots={{ day: ServerDay, calendarHeader: MiniCalendarHeader }}
      slotProps={{
        day: { highlightedDays } as Record<string, unknown>,
        calendarHeader: { onGoToToday: handleGoToToday } as Record<string, unknown>,
      }}
      showDaysOutsideCurrentMonth
      fixedWeekNumber={6}
      sx={{
        width: 260,
        height: 'auto',
        '& .MuiDayCalendar-weekDayLabel': {
          width: 32,
          height: 32,
        },
        '& .MuiPickersDay-root': {
          width: 32,
          height: 32,
        },
      }}
    />
  );
};

export default MiniCalendar;
